import React, { useState } from 'react';
import { addLeadNote, useLead } from '../../services/leads';
import { CURRENT_USER } from '../../services/currentUser';
import StatusUpdateSheet from './StatusUpdateSheet';
import AgentPicker from '../../components/AgentPicker';
import { stageOf } from './stages';
import './LeadDetails.css';

/**
 * Lead Details Page
 * Shows full inspection record details
 * Buttons: Communication, 360 View (the customer account), Form (Start Inspection), Update Status
 */

const historyText = (h) => {
  if (h.action === 'Assigned') return h.note || 'Agent assigned';
  if (h.action !== 'Reschedule') return h.action;
  const fmt = (iso) =>
    new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return `Rescheduled ${fmt(h.from)} → ${fmt(h.to)}`;
};

const LeadDetails = ({ inspection: initial, onBack, onStartInspection, onOpenCommunication, onOpenAccount }) => {
  const [notes, setNotes] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  // live copy — a status change shows here (and on the route page) immediately
  const inspection = useLead(initial.id) || initial;
  return (
    <div className="lead-details">
      <div className="lead-details-header">
        <button className="back-btn" onClick={onBack}>← Back</button>
        <h2>{inspection.contactName}</h2>
        <span className={`status-badge ${stageOf(inspection).tone}`}>{inspection.stage}</span>
      </div>

      <div className="lead-details-content">
        {/* Booking Notes on the Zoho Inspection: the customer's own note from the website, or what the
            office / an agent wrote when booking — first thing on the page, before the agent drives over */}
        {inspection.bookingNotes && (
          <div className="booking-notes-banner">
            <label>📝 Booking notes</label>
            <p>{inspection.bookingNotes}</p>
          </div>
        )}

        {/* CUSTOMER CARD */}
        <div className="info-card customer-card">
          <h3>Customer Information</h3>
          <div className="info-row">
            <label>Name:</label>
            <span>{inspection.contactName}</span>
          </div>
          <div className="info-row">
            <label>Phone:</label>
            <span>{inspection.servicePhone ? <a className="tap-link" href={`tel:${inspection.servicePhone}`}>{inspection.servicePhone}</a> : '—'}</span>
          </div>
          <div className="info-row">
            <label>Email:</label>
            <span>{inspection.email}</span>
          </div>
          <div className="info-row">
            <label>Address:</label>
            <span>{inspection.serviceAddress}</span>
          </div>
        </div>

        {/* ACTION BUTTONS - PROMINENT */}
        <div className="action-buttons">
          {/* directions from where the agent is now — opens the Google Maps / Apple Maps app on a phone */}
          {inspection.serviceAddress && (
            <a
              className="action-btn navigate-btn"
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(inspection.serviceAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              🧭 Navigate
            </a>
          )}
          <button className="action-btn communication-btn" onClick={onOpenCommunication}>💬 Communication</button>
          {/* everything about this customer — the account page (the map is one tap from there) */}
          <button className="action-btn view360-btn" onClick={onOpenAccount}>
            🔍 360° View
          </button>
          <button className="action-btn start-btn" onClick={onStartInspection}>
            📋 Start Inspection
          </button>
          <button className="action-btn status-btn" onClick={() => setStatusOpen(true)}>⚙️ Update Status</button>
        </div>

        {/* APPOINTMENT CARD - BELOW */}
        {/* own class name — "appointment-card" is the route list's card and pulled in its two-column layout */}
        <div className="info-card appointment-info-card">
          <h3>Appointment Details</h3>
          <div className="info-row">
            <label>Date:</label>
            <span>{new Date(inspection.appointmentStart).toLocaleDateString()}</span>
          </div>
          <div className="info-row">
            <label>Time:</label>
            <span>
              {new Date(inspection.appointmentStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {' - '}
              {new Date(inspection.appointmentEnd).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="info-row">
            <label>Agent:</label>
            {CURRENT_USER.admin ? (
              <button type="button" className={`agent-pick${inspection.agentName ? '' : ' unassigned'}`} onClick={() => setAssigning(true)}>
                {inspection.agentName || 'Not assigned'} <span aria-hidden="true">✎</span>
              </button>
            ) : (
              <span className={inspection.agentName ? '' : 'unassigned'}>{inspection.agentName || 'Not assigned'}</span>
            )}
          </div>
          <div className="info-row">
            <label>Zone:</label>
            <span>{inspection.zone || '-'}</span>
          </div>
          <div className="info-row">
            <label>Status:</label>
            <span className={`lead-stage ${stageOf(inspection).tone}`}>{stageOf(inspection).label}</span>
          </div>

          {/* every status change, with its note */}
          {inspection.statusHistory && inspection.statusHistory.length > 0 && (
            <div className="status-history">
              <label>Status history:</label>
              {[...inspection.statusHistory].reverse().map((h) => (
                <div key={h.at} className={`status-history-item ${{ Cancelled: 'cancelled', Rebook: 'moved', 'Report Sent': 'sent' }[h.action] || 'booked'}`}>
                  <div className="status-history-top">
                    <b>{historyText(h)}</b>
                    <span>
                      {new Date(h.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                  {h.note && <p>{h.note}</p>}
                </div>
              ))}
            </div>
          )}

          {/* the customer's answers when booking on the website (concern, home type, …) */}
          <div className="customer-notes">
            <label>Customer notes:</label>
            {inspection.customerNotes ? (
              <p className="customer-notes-text">“{inspection.customerNotes}”</p>
            ) : (
              <p className="customer-notes-empty">No notes from the customer yet.</p>
            )}
          </div>
        </div>

        {/* INSPECTION DATA (if available) */}
        {inspection.atticSizeSqFt && (
          <div className="info-card inspection-card">
            <h3>Inspection Data</h3>
            <div className="info-row">
              <label>Attic Size:</label>
              <span>{inspection.atticSizeSqFt} sq ft</span>
            </div>
            <div className="info-row">
              <label>Home Type:</label>
              <span>{inspection.typeOfHome || '-'}</span>
            </div>
            <div className="info-row">
              <label>Insulation Type:</label>
              <span>{inspection.insulationType || '-'}</span>
            </div>
          </div>
        )}

        {statusOpen && (
          <StatusUpdateSheet lead={inspection} onClose={() => setStatusOpen(false)} onSaved={() => setStatusOpen(false)} />
        )}

        {/* NOTES — saved on the lead and synced to Zoho */}
        <div className="notes-card">
          <h3>Notes</h3>
          <textarea
            className="notes-input"
            placeholder="Add notes about this inspection..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <button
            type="button"
            className="notes-save"
            disabled={!notes.trim() || noteSaving}
            onClick={async () => {
              setNoteSaving(true);
              setNoteError('');
              try {
                // shared admin login signs as "Admin" (same as the note in Zoho)
                await addLeadNote(inspection.id, notes, CURRENT_USER.admin ? 'Admin' : CURRENT_USER.firstName);
                setNotes('');
              } catch (err) {
                setNoteError(err.message || 'Couldn’t save the note');
              } finally {
                setNoteSaving(false);
              }
            }}
          >
            {noteSaving ? 'Saving…' : 'Save note'}
          </button>
          {noteError && <p className="status-error">{noteError}</p>}
          {[...(inspection.notes || [])].reverse().map((n) => (
            <div key={n.id} className="lead-note">
              <div className="lead-note-head">
                <b>{n.author}</b>
                <span>{new Date(n.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              </div>
              <p>{n.text}</p>
            </div>
          ))}
        </div>
      </div>
      {assigning && <AgentPicker lead={inspection} onClose={() => setAssigning(false)} />}
    </div>
  );
};

export default LeadDetails;
